import type { ColumnDef } from '@tanstack/react-table'
import { useMemo } from 'react'

import {
  DataTable,
  type DataTableColumns,
  downloadRowsCsv,
  downloadRowsXlsx,
  tableExportFilename,
} from '@/components/ui'
import { useProject, useStatements } from '@/modules/projects'
import { cn, humanizeText, numberToCurrency } from '@/utils'
import type {
  ReportCitation,
  ReportTableColumn,
  ReportTableSpec,
} from '../../stores/types'
import { resolveStatementSource } from './citations'
import { DownloadMenu } from './DownloadMenu'
import { fullSetFiltersQuery } from './filtersLink'
import { formatCell, isNumeric } from './format'

interface ReportTableProps {
  slug: string
  spec: ReportTableSpec
  // Maps a report section id to its heading, so `section_ref` cells
  // (the recommendations' evidence column) can link to the section.
  sectionLinks?: Record<string, string>
  // The recommendations "next steps" list is read top to bottom, so it
  // shows every row instead of paginating.
  paginate?: boolean
}

// A row DataTable can identify. Real exhibit rows already carry the
// transaction `id` (used for citation lookup); synthesized rows get an
// index id so pagination and keys still work.
type ReportRow = Record<string, unknown> & { id: string | number }

const PAGE_SIZE = 10
const PAGE_SIZES = [10, 25, 50]

export function ReportTable({
  slug,
  spec,
  sectionLinks,
  paginate = true,
}: ReportTableProps) {
  const statements = useStatements()

  const columnDefs = Array.isArray(spec.columns) ? spec.columns : []
  const specRows = Array.isArray(spec.rows) ? spec.rows : []
  const omittedCount = spec.omitted_row_count ?? 0
  const citations = useMemo(
    () => (Array.isArray(spec.citations) ? spec.citations : []),
    [spec.citations],
  )
  const showSources = citations.length > 0

  const rows = useMemo<ReportRow[]>(
    () =>
      specRows.map((row, index) => ({
        ...row,
        id: isIdValue(row.id) ? row.id : `row-${index}`,
      })),
    [specRows],
  )

  const citationByRowId = useMemo(
    () =>
      new Map(
        citations
          .filter((citation) => citation.transaction_id)
          .map((citation) => [String(citation.transaction_id), citation]),
      ),
    [citations],
  )

  const layout = useMemo(
    () => computeColumnLayout(columnDefs, rows, showSources),
    [columnDefs, rows, showSources],
  )

  const columns = useMemo<DataTableColumns>(
    () => buildColumns({ layout, sectionLinks, citationByRowId, statements }),
    [layout, sectionLinks, citationByRowId, statements],
  )

  // Only tables long enough to page get a fixed height. That keeps the
  // box a constant size as you move between pages (a last page with one
  // row would otherwise collapse it); short tables just fit their rows.
  const willPaginate = paginate && rows.length > PAGE_SIZE

  if (columnDefs.length === 0) return null
  // A table with nothing to show (no rows, no total, no omitted-rows
  // link) reads as broken — skip it rather than render a bare header.
  if (rows.length === 0 && !spec.total_row && omittedCount <= 0) return null

  return (
    <figure className="space-y-3">
      <figcaption className="space-y-1">
        <span className="block text-pretty font-semibold text-blue-7">
          {spec.title?.trim() || 'Table'}
        </span>
        {spec.claim && (
          <span className="block text-pretty text-sm leading-relaxed text-muted-foreground">
            {spec.claim}
          </span>
        )}
      </figcaption>

      {(rows.length > 0 || spec.total_row) && (
        <div className="overflow-hidden rounded-lg border border-border">
          {rows.length > 0 ? (
            <DataTable
              columns={columns}
              data={rows}
              height={willPaginate ? 'h-[28rem]' : 'max-h-none'}
              enableRowSelection={false}
              initialPageSize={paginate ? PAGE_SIZE : rows.length + 1}
              pageSizes={paginate ? PAGE_SIZES : [rows.length + 1]}
              footer={<TotalRow spec={spec} layout={layout} withDivider />}
            />
          ) : (
            <TotalRow spec={spec} layout={layout} withDivider={false} />
          )}
        </div>
      )}

      <div className="flex items-center gap-3">
        <FullSetFooter slug={slug} spec={spec} />
        {rows.length > 0 && (
          <div className="ml-auto">
            <TableDownloadMenu spec={spec} />
          </div>
        )}
      </div>
    </figure>
  )
}

const DATE_COLUMN_WIDTH = 128
const AMOUNT_COLUMN_WIDTH = 132
const SOURCE_COLUMN_WIDTH = 220

// Rough character width at text-sm plus cell/header padding, used to size
// a text column to its content instead of letting a short column ("5693"
// under "Account") stretch to an equal share of the table.
const CHAR_WIDTH = 7.2
const CELL_PADDING = 24
const HEADER_PADDING = 44
const TEXT_COLUMN_MIN = 72
const TEXT_COLUMN_MAX = 260

interface ColumnLayoutEntry {
  column: ReportTableColumn
  width: number
  numeric: boolean
  fluid: boolean
}

interface ColumnLayout {
  entries: ColumnLayoutEntry[]
  sourceWidth: number | null
}

// One source of truth for widths, shared by the DataTable columns and the
// total row below it so they line up. Dates and amounts take a fixed
// width; text columns are sized to their longest value, and the single
// widest text column flexes to fill the leftover room so the table has no
// dead space on the right.
function computeColumnLayout(
  columns: ReportTableColumn[],
  rows: ReportRow[],
  showSources: boolean,
): ColumnLayout {
  const fluidKey = widestTextColumnKey(columns, rows)
  const entries = columns.map((column) => {
    const numeric = isNumeric(column.format)
    return {
      column,
      numeric,
      fluid: column.key === fluidKey,
      width: columnWidth(column, rows, numeric),
    }
  })
  return { entries, sourceWidth: showSources ? SOURCE_COLUMN_WIDTH : null }
}

interface BuildColumnsArgs {
  layout: ColumnLayout
  sectionLinks?: Record<string, string>
  citationByRowId: Map<string, ReportCitation>
  statements: ReturnType<typeof useStatements>
}

// Format in the cell renderer rather than pre-formatting the data so
// DataTable sorts on the raw values. Currency cells keep the app's
// red/green tone; `section_ref` cells become in-page evidence links.
function buildColumns({
  layout,
  sectionLinks,
  citationByRowId,
  statements,
}: BuildColumnsArgs): DataTableColumns {
  const dataColumns: ColumnDef<ReportRow, unknown>[] = layout.entries.map(
    ({ column, numeric, fluid, width }) => ({
      accessorKey: column.key,
      header: column.label,
      size: width,
      meta: isTextColumn(column.format)
        ? { wrap: true, ...(fluid ? { isFluid: true } : {}) }
        : undefined,
      cell: ({ getValue }) => {
        const value = getValue()
        if (column.format === 'section_ref') {
          return <EvidenceLinks value={value} sectionLinks={sectionLinks} />
        }
        return (
          <div
            className={cn(
              'w-full text-pretty',
              numeric && 'text-right tabular-nums',
              currencyTone(value, column.format),
            )}
          >
            {formatCell(value, column.format)}
          </div>
        )
      },
    }),
  )

  if (layout.sourceWidth !== null) {
    dataColumns.push({
      id: '__source',
      header: 'Source',
      size: layout.sourceWidth,
      meta: { wrap: true },
      cell: ({ row }) => (
        <SourceCell
          citation={citationByRowId.get(String(row.original.id ?? ''))}
          statements={statements}
        />
      ),
    })
  }

  return dataColumns as DataTableColumns
}

function columnWidth(
  column: ReportTableColumn,
  rows: ReportRow[],
  numeric: boolean,
): number {
  if (numeric) return AMOUNT_COLUMN_WIDTH
  if (column.format === 'date') return DATE_COLUMN_WIDTH
  return estimatedTextWidth(column, rows)
}

// Width that fits the header and the longest cell value, clamped so no
// text column runs away or collapses. The widest column overrides this by
// flexing, so this mainly right-sizes the shorter text columns.
function estimatedTextWidth(
  column: ReportTableColumn,
  rows: ReportRow[],
): number {
  const headerWidth = column.label.length * CHAR_WIDTH + HEADER_PADDING
  const contentWidth =
    longestValueLength(column, rows) * CHAR_WIDTH + CELL_PADDING
  const natural = Math.max(headerWidth, contentWidth)
  return Math.min(
    TEXT_COLUMN_MAX,
    Math.max(TEXT_COLUMN_MIN, Math.round(natural)),
  )
}

function widestTextColumnKey(
  columns: ReportTableColumn[],
  rows: ReportRow[],
): string | null {
  let widestKey: string | null = null
  let widestLength = -1
  for (const column of columns) {
    if (!isTextColumn(column.format)) continue
    const length = longestValueLength(column, rows)
    if (length > widestLength) {
      widestLength = length
      widestKey = column.key
    }
  }
  return widestKey
}

function longestValueLength(
  column: ReportTableColumn,
  rows: ReportRow[],
): number {
  let longest = 0
  for (const row of rows) {
    const text = formatCell(row[column.key], column.format)
    if (text.length > longest) longest = text.length
  }
  return longest
}

// Text and evidence-link columns hold the variable-length content, so
// they are the ones that flex; dates and amounts stay a fixed width.
function isTextColumn(format?: ReportTableColumn['format']): boolean {
  return !isNumeric(format) && format !== 'date'
}

// total_row is the table's footer — DataTable has no footer-row
// affordance, so it renders flush at the bottom of the same bordered box
// (a top divider, not a separate card) and reuses the column layout and
// cell padding so each total lands under its column.
function TotalRow({
  spec,
  layout,
  withDivider,
}: {
  spec: ReportTableSpec
  layout: ColumnLayout
  withDivider: boolean
}) {
  if (!spec.total_row) return null
  return (
    <div
      className={cn(
        'flex items-start bg-muted/40 px-4 py-2.5 text-sm font-semibold text-blue-7',
        withDivider && 'border-t border-border',
      )}
    >
      {layout.entries.map(({ column, numeric, fluid, width }) => (
        <div
          key={column.key}
          className={cn(
            'px-2 first:pl-0 text-pretty',
            fluid ? 'flex-1' : 'shrink-0',
            numeric && 'text-right tabular-nums',
            currencyTone(spec.total_row?.[column.key], column.format),
          )}
          style={fluid ? undefined : { width }}
        >
          {formatCell(spec.total_row?.[column.key], column.format)}
        </div>
      ))}
      {layout.sourceWidth !== null && (
        <div className="shrink-0 px-2" style={{ width: layout.sourceWidth }} />
      )}
    </div>
  )
}

function TableDownloadMenu({ spec }: { spec: ReportTableSpec }) {
  const project = useProject()
  const table = {
    columns: spec.columns,
    rows: Array.isArray(spec.rows) ? spec.rows : [],
    totalRow: spec.total_row,
    title: spec.title,
  }
  const baseFilename = tableExportFilename(spec.title, 'table', project?.name)
  return (
    <DownloadMenu
      label="Download table"
      options={[
        {
          label: 'Download as CSV',
          onSelect: () => downloadRowsCsv(table, `${baseFilename}.csv`),
        },
        {
          label: 'Download as Excel',
          onSelect: () => downloadRowsXlsx(table, `${baseFilename}.xlsx`),
        },
      ]}
    />
  )
}

function isIdValue(value: unknown): value is string | number {
  return typeof value === 'string' || typeof value === 'number'
}

// Money follows the app convention (see AmountCell): outflows red,
// inflows green, zero stays in ink. Only currency columns get a tone.
function currencyTone(value: unknown, format?: ReportTableColumn['format']) {
  if (format !== 'currency' || typeof value !== 'number') return ''
  if (value < 0) return 'text-red-600'
  if (value > 0) return 'text-emerald-600'
  return ''
}

interface SourceCellProps {
  citation?: ReportCitation
  statements: ReturnType<typeof useStatements>
}

function SourceCell({ citation, statements }: SourceCellProps) {
  if (!citation) return null
  const source = resolveStatementSource(statements, citation)
  if (!source) return null
  if (!source.url) {
    return <span className="text-sm text-muted-foreground">{source.label}</span>
  }
  return (
    <a
      href={source.url}
      target="_blank"
      rel="noopener noreferrer"
      className="text-sm text-brand underline-offset-2 hover:underline cursor-pointer"
    >
      {source.label}
    </a>
  )
}

interface FullSetFooterProps {
  slug: string
  spec: ReportTableSpec
}

// Every transaction exhibit must be chase-able: if rows were capped,
// link to the omitted remainder; if it was fully listed but the class
// is filterable, still offer a live link so the reader can sort, export,
// or dig in the Transactions view.
function FullSetFooter({ slug, spec }: FullSetFooterProps) {
  const count = spec.omitted_row_count ?? 0
  const query = fullSetFiltersQuery(spec.full_set_filters)
  const href = query ? `/projects/${slug}?${query}` : null

  if (count > 0) {
    const total =
      typeof spec.omitted_total === 'number'
        ? ` totaling ${numberToCurrency(spec.omitted_total)}`
        : ''
    const text = `…and ${count.toLocaleString()} more${total}`
    if (!href) return <p className="text-sm text-muted-foreground">{text}</p>
    return (
      <p className="text-sm text-muted-foreground">
        <a href={href} className={fullSetLinkClass}>
          {`${text} in the Transactions table`}
        </a>
      </p>
    )
  }

  if (!href) return null
  const shown = Array.isArray(spec.rows) ? spec.rows.length : 0
  return (
    <p className="text-sm text-muted-foreground">
      <a href={href} className={fullSetLinkClass}>
        {shown > 0
          ? `Open all ${shown.toLocaleString()} in the Transactions table`
          : 'Open in the Transactions table'}
      </a>
    </p>
  )
}

const fullSetLinkClass =
  'text-brand underline-offset-2 hover:underline cursor-pointer'

interface EvidenceLinksProps {
  value: unknown
  sectionLinks?: Record<string, string>
}

// Renders a recommendation's evidence cell: each section id becomes a
// link that scrolls to that section. An id with no matching section
// renders as plain text rather than a link that goes nowhere.
function EvidenceLinks({ value, sectionLinks }: EvidenceLinksProps) {
  const ids = toSectionIdList(value)
  if (ids.length === 0) return null
  return (
    <span className="flex flex-col gap-1">
      {ids.map((id) => {
        const title = sectionLinks?.[id]
        return title ? (
          <a
            key={id}
            href={`#${id}`}
            onClick={scrollToSection(id)}
            className="text-brand underline-offset-2 hover:underline cursor-pointer"
          >
            {title}
          </a>
        ) : (
          <span key={id} className="text-muted-foreground">
            {humanizeText(id)}
          </span>
        )
      })}
    </span>
  )
}

function scrollToSection(id: string) {
  return (event: React.MouseEvent<HTMLAnchorElement>) => {
    const target = document.getElementById(id)
    if (!target) return
    event.preventDefault()
    target.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }
}

// The cell arrives as a JSON array of section ids, or a comma-separated
// string as a fallback.
function toSectionIdList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return value.map((entry) => String(entry).trim()).filter(Boolean)
  }
  if (typeof value !== 'string') return []
  const trimmed = value.trim()
  if (trimmed.startsWith('[')) {
    try {
      const parsed = JSON.parse(trimmed)
      if (Array.isArray(parsed)) {
        return parsed.map((entry) => String(entry).trim()).filter(Boolean)
      }
    } catch {
      // fall through to comma splitting
    }
  }
  return trimmed
    ? trimmed
        .split(',')
        .map((entry) => entry.trim())
        .filter(Boolean)
    : []
}
